import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Wallet, Download, PieChart, ShoppingBag, Eye, EyeOff, AlertTriangle, CreditCard, Users, FileText, Clock, Truck, Calendar, Phone, MapPin } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, AreaChart, Area, PieChart as RechartsPie, Pie, Cell } from 'recharts';
import LembretesWidget from '@/components/LembretesWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Formatador de moeda com opção de ocultar
const formatCurrency = (value, hidden = false) => {
  if (hidden) return '•••••';
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Custom tooltip para gráficos
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-lg">
        <p className="text-sm text-muted-foreground mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm font-medium">
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [bills, setBills] = useState([]);
  const [resumoFinanceiro, setResumoFinanceiro] = useState(null);
  const [faturamentoAnual, setFaturamentoAnual] = useState(null);
  const [vendasSemanais, setVendasSemanais] = useState([]);
  const [entregasSemana, setEntregasSemana] = useState({ entregas: [], total: 0, qtd_atrasados: 0, qtd_semana: 0, periodo: {} });
  const [loading, setLoading] = useState(true);
  // Inicializar com o mês atual (formato '01', '02', etc.)
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const month = new Date().getMonth() + 1;
    return month.toString().padStart(2, '0');
  });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [hideValues, setHideValues] = useState(() => {
    // Recuperar preferência do localStorage - separado do Financeiro
    const saved = localStorage.getItem('hideDashboardValues');
    return saved === 'true';
  });

  // Salvar preferência quando mudar
  const toggleHideValues = () => {
    const newValue = !hideValues;
    setHideValues(newValue);
    localStorage.setItem('hideDashboardValues', newValue.toString());
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Construir parâmetros de data baseado no ano e mês selecionados
      let params = '';
      if (selectedMonth && selectedYear) {
        const startDate = `${selectedYear}-${selectedMonth}-01`;
        // Calcular o último dia do mês corretamente
        const lastDay = new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate();
        const endDate = `${selectedYear}-${selectedMonth}-${lastDay.toString().padStart(2, '0')}`;
        params = `date_start=${startDate}&date_end=${endDate}`;
      } else if (selectedYear) {
        params = `date_start=${selectedYear}-01-01&date_end=${selectedYear}-12-31`;
      }
      
      // Carregar stats (essencial)
      try {
        const statsRes = await axios.get(`${API}/dashboard/stats?${params}`);
        setStats(statsRes.data);
      } catch (statsError) {
        console.error('Erro ao carregar stats:', statsError.message);
        // Não mostrar toast para não duplicar erros
      }
      
      // Carregar orders separadamente (usando all_data para compatibilidade)
      try {
        const ordersRes = await axios.get(`${API}/orders`, { params: { all_data: true } });
        // A resposta pode ser array (all_data=true) ou objeto paginado
        const ordersData = Array.isArray(ordersRes.data) ? ordersRes.data : (ordersRes.data.orders || []);
        setOrders(ordersData);
      } catch (ordersError) {
        console.error('Erro ao carregar orders:', ordersError.message);
        setOrders([]);
      }
      
      // Carregar bills separadamente com fallback
      try {
        const billsRes = await axios.get(`${API}/bills`);
        setBills(billsRes.data.filter(b => b.status === 'pendente'));
      } catch (billsError) {
        console.warn('Erro ao carregar bills, tentando endpoint alternativo:', billsError.message);
        try {
          const billsRes2 = await axios.get(`${API}/bills/v2`);
          const billsData = billsRes2.data.bills || billsRes2.data || [];
          setBills(billsData.filter(b => b.status === 'pendente'));
        } catch (e) {
          console.warn('Endpoint alternativo também falhou:', e.message);
          setBills([]);
        }
      }
      
      // Carregar resumo financeiro (contas, cheques, recebimentos, parcelas)
      try {
        const resumoRes = await axios.get(`${API}/dashboard/resumo-financeiro`);
        setResumoFinanceiro(resumoRes.data);
      } catch (resumoError) {
        console.warn('Erro ao carregar resumo financeiro:', resumoError.message);
        setResumoFinanceiro(null);
      }
      
      // Carregar faturamento anual (2025, 2026 e últimos 12 meses)
      try {
        const faturamentoRes = await axios.get(`${API}/dashboard/faturamento-anual`);
        setFaturamentoAnual(faturamentoRes.data);
      } catch (faturamentoError) {
        console.warn('Erro ao carregar faturamento anual:', faturamentoError.message);
        setFaturamentoAnual(null);
      }
      
      // Carregar vendas semanais
      try {
        const vendasSemanaisRes = await axios.get(`${API}/dashboard/vendas-semanais?semanas=8`);
        setVendasSemanais(vendasSemanaisRes.data.semanas || []);
      } catch (vendasError) {
        console.warn('Erro ao carregar vendas semanais:', vendasError.message);
        setVendasSemanais([]);
      }
      
      // Carregar entregas da semana
      try {
        const entregasRes = await axios.get(`${API}/dashboard/entregas-semana`);
        setEntregasSemana(entregasRes.data);
      } catch (entregasError) {
        console.warn('Erro ao carregar entregas da semana:', entregasError.message);
        setEntregasSemana({ entregas: [], total: 0, qtd_atrasados: 0, qtd_semana: 0, periodo: {} });
      }
    } catch (error) {
      console.error('Erro geral:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/reports/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio_financeiro.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Relatório exportado!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    }
  };

  // Gerar anos disponíveis
  const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());
  const months = [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' }
  ];

  // Dados do gráfico de vendas por tipo
  const paymentTypeData = stats?.vendas_por_tipo 
    ? Object.entries(stats.vendas_por_tipo).map(([tipo, valor]) => ({
        tipo: tipo.charAt(0).toUpperCase() + tipo.slice(1),
        valor: valor
      }))
    : [];

  // Gráfico de vendas dos pedidos
  // Dados históricos de vendas de 2025 (ano anterior)
  const vendas2025 = [
    { mes: '2025-07', mesLabel: 'Jul/25', vendas: 131805 },
    { mes: '2025-08', mesLabel: 'Ago/25', vendas: 103634 },
    { mes: '2025-09', mesLabel: 'Set/25', vendas: 111620 },
    { mes: '2025-10', mesLabel: 'Out/25', vendas: 95320 },
    { mes: '2025-11', mesLabel: 'Nov/25', vendas: 151730 },
    { mes: '2025-12', mesLabel: 'Dez/25', vendas: 129880 }
  ];

  // Gráfico de vendas por mês - com dados históricos de 2025
  const ordersChartData = (() => {
    // Agregar vendas do ano atual por mês
    const currentYearData = orders.reduce((acc, order) => {
      const month = order.data_pedido?.substring(0, 7);
      if (month && month.startsWith('2026')) {
        const existing = acc.find(a => a.mes === month);
        const valor = order.total_com_desconto || order.total_venda || 0;
        if (existing) {
          existing.vendas += valor;
        } else {
          const mesNum = parseInt(month.substring(5));
          const mesesNomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
          acc.push({ 
            mes: month, 
            mesLabel: `${mesesNomes[mesNum - 1]}/26`,
            vendas: valor 
          });
        }
      }
      return acc;
    }, []).sort((a, b) => a.mes.localeCompare(b.mes));

    // Combinar dados de 2025 + 2026
    return [...vendas2025, ...currentYearData];
  })();

  // Gráfico de vendas por dia - todos os dias do mês selecionado
  const dailySalesData = (() => {
    // Determinar o mês a ser exibido
    const targetYear = parseInt(selectedYear) || new Date().getFullYear();
    const targetMonth = selectedMonth ? parseInt(selectedMonth) - 1 : new Date().getMonth();
    
    // Obter o número de dias no mês
    const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
    
    // Criar array com todos os dias do mês
    const allDays = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      allDays.push({
        data: dateStr,
        vendas: 0,
        pedidos: 0,
        dia: String(day).padStart(2, '0')
      });
    }
    
    // Preencher com os dados reais de vendas
    orders.forEach(order => {
      const date = order.data_pedido?.substring(0, 10);
      if (date) {
        const orderYear = parseInt(date.substring(0, 4));
        const orderMonth = parseInt(date.substring(5, 7)) - 1;
        
        if (orderYear === targetYear && orderMonth === targetMonth) {
          const dayIndex = parseInt(date.substring(8, 10)) - 1;
          if (allDays[dayIndex]) {
            const valor = order.total_com_desconto || order.total_venda || 0;
            allDays[dayIndex].vendas += valor;
            allDays[dayIndex].pedidos += 1;
          }
        }
      }
    });
    
    return allDays;
  })();

  const COLORS = ['#34d399', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

  if (loading || !stats) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Mapa de meses abreviados
  const monthNames = {
    '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
    '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
    '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez'
  };

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header com filtros */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold font-manrope tracking-tight">Dashboard</h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Visão geral das suas finanças</p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={toggleHideValues}
              title={hideValues ? "Mostrar valores" : "Ocultar valores"}
            >
              {hideValues ? <EyeOff className="h-5 w-5 text-muted-foreground" /> : <Eye className="h-5 w-5 text-muted-foreground" />}
            </Button>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="h-9 w-auto min-w-[80px] text-xs sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedMonth || 'all'} onValueChange={(v) => setSelectedMonth(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[90px] text-xs sm:text-sm">
                <SelectValue placeholder="Mês" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {months.map(month => (
                  <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button onClick={handleExport} size="sm" className="h-9 shadow-[0_0_10px_rgba(52,211,153,0.2)]">
              <Download className="h-3.5 w-3.5 mr-1.5" />
              <span className="hidden sm:inline">Exportar</span>
            </Button>
          </div>
        </div>

        {/* KPIs Grid - 2 colunas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Vendas do Mês */}
          <Card className="border-border bg-gradient-to-br from-blue-500/10 to-transparent">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">Vendas do Mês</span>
                <PieChart className="h-4 w-4 text-blue-500" />
              </div>
              <div className="text-xl sm:text-2xl font-bold font-mono text-blue-500">
                {formatCurrency(stats.total_vendas || 0, hideValues)}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Total de pedidos no período
              </p>
            </CardContent>
          </Card>

          {/* Vendas do Dia */}
          <Card className="border-border bg-gradient-to-br from-emerald-500/10 to-transparent">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">Vendas Hoje</span>
                <ShoppingBag className="h-4 w-4 text-emerald-500" />
              </div>
              <div className="text-xl sm:text-2xl font-bold font-mono text-emerald-500">
                {formatCurrency(stats.vendas_do_dia || 0, hideValues)}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Pedidos de hoje
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Cards de Resumo Financeiro - 4 colunas */}
        {resumoFinanceiro && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Contas a Pagar */}
            <Card className={`border-border ${resumoFinanceiro.contas.vencidas.quantidade > 0 ? 'bg-gradient-to-br from-red-500/10 to-transparent' : 'bg-gradient-to-br from-orange-500/10 to-transparent'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">Contas a Pagar</span>
                  <FileText className={`h-4 w-4 ${resumoFinanceiro.contas.vencidas.quantidade > 0 ? 'text-red-500' : 'text-orange-500'}`} />
                </div>
                {resumoFinanceiro.contas.vencidas.quantidade > 0 && (
                  <div className="mb-2">
                    <div className="text-lg font-bold font-mono text-red-500">
                      {formatCurrency(resumoFinanceiro.contas.vencidas.total, hideValues)}
                    </div>
                    <p className="text-[10px] text-red-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {resumoFinanceiro.contas.vencidas.quantidade} vencida(s)
                    </p>
                  </div>
                )}
                <div className={resumoFinanceiro.contas.vencidas.quantidade > 0 ? 'border-t border-zinc-700 pt-2' : ''}>
                  <div className={`text-lg font-bold font-mono ${resumoFinanceiro.contas.vencidas.quantidade > 0 ? 'text-orange-400' : 'text-orange-500'}`}>
                    {formatCurrency(resumoFinanceiro.contas.proximas_7_dias.total, hideValues)}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    <Clock className="h-3 w-3 inline mr-1" />
                    {resumoFinanceiro.contas.proximas_7_dias.quantidade} próx. 7 dias
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Cheques */}
            <Card className={`border-border ${resumoFinanceiro.cheques.vencidos.quantidade > 0 ? 'bg-gradient-to-br from-red-500/10 to-transparent' : 'bg-gradient-to-br from-purple-500/10 to-transparent'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">Cheques</span>
                  <Wallet className={`h-4 w-4 ${resumoFinanceiro.cheques.vencidos.quantidade > 0 ? 'text-red-500' : 'text-purple-500'}`} />
                </div>
                {resumoFinanceiro.cheques.vencidos.quantidade > 0 && (
                  <div className="mb-2">
                    <div className="text-lg font-bold font-mono text-red-500">
                      {formatCurrency(resumoFinanceiro.cheques.vencidos.total, hideValues)}
                    </div>
                    <p className="text-[10px] text-red-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {resumoFinanceiro.cheques.vencidos.quantidade} vencido(s)
                    </p>
                  </div>
                )}
                <div className={resumoFinanceiro.cheques.vencidos.quantidade > 0 ? 'border-t border-zinc-700 pt-2' : ''}>
                  <div className={`text-lg font-bold font-mono ${resumoFinanceiro.cheques.vencidos.quantidade > 0 ? 'text-purple-400' : 'text-purple-500'}`}>
                    {formatCurrency(resumoFinanceiro.cheques.proximas_7_dias.total, hideValues)}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    <Clock className="h-3 w-3 inline mr-1" />
                    {resumoFinanceiro.cheques.proximas_7_dias.quantidade} próx. 7 dias
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Recebimentos Cartão */}
            <Card className="border-border bg-gradient-to-br from-cyan-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">Receber Cartão</span>
                  <CreditCard className="h-4 w-4 text-cyan-500" />
                </div>
                {resumoFinanceiro.recebimentos_cartao.hoje.quantidade > 0 && (
                  <div className="mb-2">
                    <div className="text-lg font-bold font-mono text-cyan-400">
                      {formatCurrency(resumoFinanceiro.recebimentos_cartao.hoje.total, hideValues)}
                    </div>
                    <p className="text-[10px] text-cyan-400">
                      {resumoFinanceiro.recebimentos_cartao.hoje.quantidade} a receber hoje
                    </p>
                  </div>
                )}
                <div className={resumoFinanceiro.recebimentos_cartao.hoje.quantidade > 0 ? 'border-t border-zinc-700 pt-2' : ''}>
                  <div className="text-lg font-bold font-mono text-cyan-500">
                    {formatCurrency(resumoFinanceiro.recebimentos_cartao.pendentes_total.total, hideValues)}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    {resumoFinanceiro.recebimentos_cartao.pendentes_total.quantidade} pendentes total
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Parcelas de Clientes */}
            <Card className="border-border bg-gradient-to-br from-amber-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">Parcelas Clientes</span>
                  <Users className="h-4 w-4 text-amber-500" />
                </div>
                <div className="text-xl sm:text-2xl font-bold font-mono text-amber-500">
                  {formatCurrency(resumoFinanceiro.parcelas_clientes.total, hideValues)}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {resumoFinanceiro.parcelas_clientes.quantidade} pedido(s) em aberto
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Gráfico Principal - Vendas por Dia (Full Width) */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base sm:text-lg font-manrope">
              Vendas por Dia - {months.find(m => m.value === (selectedMonth || String(new Date().getMonth() + 1).padStart(2, '0')))?.label || 'Mês Atual'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pl-0 pr-2">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={dailySalesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis 
                  dataKey="dia" 
                  stroke="#a1a1aa" 
                  fontSize={9}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.ceil(dailySalesData.length / 10)}
                />
                <YAxis 
                  stroke="#a1a1aa" 
                  fontSize={9}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}
                  width={35}
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-2 shadow-lg">
                          <p className="text-xs text-muted-foreground">Dia {label}</p>
                          <p className="text-sm font-medium text-emerald-400">
                            {formatCurrency(payload[0]?.value || 0, hideValues)}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {payload[0]?.payload?.pedidos || 0} pedido(s)
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="vendas" 
                  name="Vendas"
                  stroke="#34d399" 
                  fill="url(#colorVendas)"
                  strokeWidth={2}
                />
                <defs>
                  <linearGradient id="colorVendas" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráficos Secundários - 2 colunas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Vendas por Mês */}
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base sm:text-lg font-manrope">Vendas por Mês</CardTitle>
            </CardHeader>
            <CardContent className="pl-0 pr-2">
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={ordersChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="mesLabel" 
                    stroke="#a1a1aa" 
                    fontSize={9}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#a1a1aa" 
                    fontSize={9}
                    tickLine={false}
                    tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}
                    width={35}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="vendas" 
                    name="Vendas"
                    stroke="#3b82f6" 
                    fill="url(#colorVendasMes)"
                    strokeWidth={2}
                  />
                  <defs>
                    <linearGradient id="colorVendasMes" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Receitas por Tipo */}
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base sm:text-lg font-manrope">Por Tipo de Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="pl-2 pr-2">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={paymentTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                  <XAxis 
                    dataKey="tipo" 
                    stroke="#a1a1aa" 
                    fontSize={9}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#a1a1aa" 
                    fontSize={9}
                    tickLine={false}
                    tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}
                    width={35}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="valor" name="Valor" fill="#f59e0b" radius={[4, 4, 0, 0]} barSize={30} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* NOVO: Grid com Gráfico de Vendas Semanais e Quadro de Entregas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Gráfico de Vendas por Semana - Estilo Ondas */}
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base sm:text-lg font-manrope flex items-center gap-2">
                <Calendar className="h-5 w-5 text-emerald-500" />
                Vendas por Semana
              </CardTitle>
            </CardHeader>
            <CardContent className="pl-2 pr-4">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={vendasSemanais}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                  <XAxis 
                    dataKey="semana" 
                    stroke="#a1a1aa" 
                    fontSize={10}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#a1a1aa" 
                    fontSize={10}
                    tickLine={false}
                    tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
                    width={40}
                  />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-lg">
                            <p className="text-sm font-medium text-white">{label}</p>
                            <p className="text-xs text-zinc-400">{data.inicio} - {data.fim}</p>
                            <p className="text-sm font-medium text-emerald-400 mt-1">
                              {formatCurrency(data.valor)}
                            </p>
                            <p className="text-xs text-zinc-400">
                              {data.qtd_pedidos} pedido(s)
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area 
                    type="monotone"
                    dataKey="valor" 
                    name="Vendas" 
                    stroke="#10b981"
                    fill="url(#colorVendasSemana)"
                    strokeWidth={2}
                  />
                  <defs>
                    <linearGradient id="colorVendasSemana" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Quadro de Avisos - Entregas da Semana + Atrasados */}
          <Card className="border-border border-l-4 border-l-orange-500">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base sm:text-lg font-manrope flex items-center gap-2">
                  <Truck className="h-5 w-5 text-orange-500" />
                  Entregas Pendentes
                </CardTitle>
                <div className="flex gap-1">
                  {entregasSemana.qtd_atrasados > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {entregasSemana.qtd_atrasados} atrasado(s)
                    </Badge>
                  )}
                  {entregasSemana.qtd_semana > 0 && (
                    <Badge variant="secondary" className="bg-orange-500/20 text-orange-600 dark:text-orange-400 text-xs">
                      {entregasSemana.qtd_semana} esta semana
                    </Badge>
                  )}
                </div>
              </div>
              {entregasSemana.periodo?.inicio && (
                <p className="text-xs text-muted-foreground">
                  Semana: {entregasSemana.periodo.inicio} - {entregasSemana.periodo.fim}
                </p>
              )}
            </CardHeader>
            <CardContent className="max-h-[240px] overflow-y-auto">
              {entregasSemana.entregas.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Truck className="h-10 w-10 mb-2 opacity-30" />
                  <p className="text-sm">Nenhuma entrega pendente</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {entregasSemana.entregas.map((entrega) => (
                    <div 
                      key={entrega.id}
                      className={`p-3 rounded-lg border transition-colors ${
                        entrega.atrasado 
                          ? 'bg-red-100 dark:bg-red-500/10 border-red-300 dark:border-red-500/30' 
                          : 'bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/30 hover:border-orange-400 dark:hover:border-orange-500/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-bold text-orange-600 dark:text-orange-400">
                              {entrega.numero_pedido}
                            </span>
                            {entrega.atrasado && (
                              <Badge variant="destructive" className="text-[10px] h-5">
                                ATRASADO
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium truncate text-foreground">{entrega.cliente_nome}</p>
                          {entrega.cliente_telefone && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {entrega.cliente_telefone}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold text-orange-600 dark:text-orange-400">
                            {entrega.dia_semana}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {entrega.data_entrega}
                          </p>
                          <p className="text-xs font-semibold mt-1 text-foreground">
                            {formatCurrency(entrega.total_venda, hideValues)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cards de Faturamento Anual - Por último */}
        {faturamentoAnual && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Faturamento 2025 */}
            <Card className="border-border bg-gradient-to-br from-indigo-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">Faturamento 2025</span>
                  <Wallet className="h-4 w-4 text-indigo-500" />
                </div>
                <div className="text-xl sm:text-2xl font-bold font-mono text-indigo-500">
                  {formatCurrency(faturamentoAnual.faturamento_2025.valor, hideValues)}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {faturamentoAnual.faturamento_2025.qtd_pedidos} pedidos | Jan - Dez 2025
                </p>
              </CardContent>
            </Card>

            {/* Faturamento 2026 */}
            <Card className="border-border bg-gradient-to-br from-violet-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">Faturamento 2026</span>
                  <Wallet className="h-4 w-4 text-violet-500" />
                </div>
                <div className="text-xl sm:text-2xl font-bold font-mono text-violet-500">
                  {formatCurrency(faturamentoAnual.faturamento_2026.valor, hideValues)}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {faturamentoAnual.faturamento_2026.qtd_pedidos} pedidos | Jan - Dez 2026
                </p>
              </CardContent>
            </Card>

            {/* Faturamento Últimos 12 Meses */}
            <Card className="border-border bg-gradient-to-br from-teal-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">Últimos 12 Meses</span>
                  <Clock className="h-4 w-4 text-teal-500" />
                </div>
                <div className="text-xl sm:text-2xl font-bold font-mono text-teal-500">
                  {formatCurrency(faturamentoAnual.faturamento_12_meses.valor, hideValues)}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {faturamentoAnual.faturamento_12_meses.qtd_pedidos} pedidos | Média: {formatCurrency(faturamentoAnual.faturamento_12_meses.media_mensal, hideValues)}/mês
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Lembretes Widget (posição final do dashboard, modo compacto) */}
        <LembretesWidget compact />

      </div>
    </Layout>
  );
}
